import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
 Upload, 
 FolderPlus, 
 Folder, 
 File, 
 FileAudio, 
 Image, 
 Video,
 Download, 
 Trash2, 
 Edit3,
 Search,
 Grid,
 List,
 ChevronRight,
 ArrowLeft,
 MoreVertical,
 Play,
 FileText,
 Archive
} from 'lucide-react';

export default function FileManagerPage() {
 const location = useLocation();
 const navigate = useNavigate();
 const { t } = useTranslation();
 const [currentView, setCurrentView] = useState('file-manager');
 const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
 const [currentPath, setCurrentPath] = useState('/');
 const [selectedItems, setSelectedItems] = useState([]);
 const [searchTerm, setSearchTerm] = useState('');
 const [showUploadModal, setShowUploadModal] = useState(false);
 const [showNewFolderModal, setShowNewFolderModal] = useState(false);
 const [newFolderName, setNewFolderName] = useState('');

 // 根据路径确定当前视图
 useEffect(() => {
 const path = location.pathname;
 if (path.includes('/file-manager')) setCurrentView('file-manager');
 else if (path.includes('/uploads')) setCurrentView('uploads');
 else if (path.includes('/audio-library')) setCurrentView('audio-library');
 else if (path.includes('/projects')) setCurrentView('projects');
 else setCurrentView('file-manager');
 }, [location]);

 // 模拟文件系统数据
 const [fileSystem, setFileSystem] = useState({
 '/': {
 folders: [
 { id: 1, name: '我的播客项目', created: '2024-01-15', size: '156 MB' },
 { id: 2, name: '音频素材', created: '2024-01-20', size: '2.3 GB' },
 { id: 3, name: '背景音乐', created: '2024-02-01', size: '453 MB' },
 { id: 4, name: '录音存档', created: '2024-02-10', size: '1.2 GB' }
 ],
 files: [
 { id: 1, name: '开场白.mp3', type: 'audio', size: '12.5 MB', created: '2024-02-15' },
 { id: 2, name: '播客封面.jpg', type: 'image', size: '2.1 MB', created: '2024-02-14' },
 { id: 3, name: '节目大纲.docx', type: 'document', size: '856 KB', created: '2024-02-13' },
 { id: 4, name: '嘉宾采访.mp4', type: 'video', size: '345 MB', created: '2024-02-12' }
 ]
 }
 });

 // 获取文件图标
 const getFileIcon = (type) => {
 switch (type) {
 case 'audio':
 return <FileAudio className="w-6 h-6 text-green-500" />;
 case 'image':
 return <Image className="w-6 h-6 text-accent-500" />;
 case 'video':
 return <Video className="w-6 h-6 text-red-500" />;
 case 'document':
 return <FileText className="w-6 h-6 text-accent-500" />;
 default:
 return <File className="w-6 h-6 text-ink-500" />;
 }
 };

 // 处理文件上传
 const handleFileUpload = (event) => {
 const files = Array.from(event.target.files);
 // 这里应该实现实际的文件上传逻辑
 console.log('上传文件:', files);
 setShowUploadModal(false);
 };

 // 创建新文件夹
 const handleCreateFolder = () => {
 if (newFolderName.trim()) {
 // 这里应该实现实际的文件夹创建逻辑
 console.log('创建文件夹:', newFolderName);
 setNewFolderName('');
 setShowNewFolderModal(false);
 }
 };

 // 渲染文件管理器主界面
 const renderFileManager = () => (
 <div className="space-y-6">
 {/* 工具栏 */}
 <div className="bg-cream-50 rounded-2xl shadow-sm border p-4">
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-4">
 <h1 className="text-xl font-semibold text-ink-900">
 {t('fileManager.title', '文件管理器')}
 </h1>
 <div className="text-sm text-ink-500">
 {currentPath === '/' ? t('fileManager.rootFolder', '根目录') : currentPath}
 </div>
 </div>
 
 <div className="flex items-center gap-2">
 <button
 onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
 className="p-2 text-ink-500 hover:text-ink-700 hover:bg-cream-100 rounded-lg transition-colors"
 title={viewMode === 'grid' ? t('fileManager.listView', '列表视图') : t('fileManager.gridView', '网格视图')}
 >
 {viewMode === 'grid' ? <List className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
 </button>
 </div>
 </div>

 <div className="flex items-center gap-3">
 <button
 onClick={() => setShowUploadModal(true)}
 className="bg-accent-600 text-white px-4 py-2 rounded-lg hover:bg-accent-700 transition-colors flex items-center gap-2"
 >
 <Upload className="w-4 h-4" />
 {t('fileManager.uploadFiles', '上传文件')}
 </button>
 
 <button
 onClick={() => setShowNewFolderModal(true)}
 className="bg-cream-50 border border-ink-300 text-ink-700 px-4 py-2 rounded-lg hover:bg-cream-100 transition-colors flex items-center gap-2"
 >
 <FolderPlus className="w-4 h-4" />
 {t('fileManager.newFolder', '新建文件夹')}
 </button>

 <div className="ml-auto flex items-center gap-2">
 <div className="relative">
 <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400" />
 <input
 type="text"
 placeholder={t('fileManager.search', '搜索文件...')}
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10 pr-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
 />
 </div>
 </div>
 </div>
 </div>

 {/* 文件列表 */}
 <div className="bg-cream-50 rounded-2xl shadow-sm border">
 {/* 文件夹 */}
 {fileSystem[currentPath]?.folders?.length > 0 && (
 <div className="p-4">
 <h3 className="text-sm font-medium text-ink-900 mb-3 flex items-center gap-2">
 <Folder className="w-4 h-4" />
 {t('fileManager.folders', '文件夹')}
 </h3>
 <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4' : 'space-y-2'}>
 {fileSystem[currentPath].folders.map(folder => (
 <div
 key={folder.id}
 className={`group cursor-pointer ${
 viewMode === 'grid' 
 ? 'p-3 rounded-lg hover:bg-cream-100 text-center' 
 : 'flex items-center gap-3 p-3 hover:bg-cream-100 rounded-lg'
 }`}
 >
 <Folder className={`${viewMode === 'grid' ? 'w-8 h-8 mx-auto mb-2' : 'w-5 h-5'} text-accent-500`} />
 <div className={viewMode === 'grid' ? '' : 'flex-1'}>
 <div className="font-medium text-sm text-ink-900 truncate">{folder.name}</div>
 <div className="text-xs text-ink-500">{folder.size}</div>
 </div>
 {viewMode === 'list' && (
 <div className="text-xs text-ink-500">{folder.created}</div>
 )}
 </div>
 ))}
 </div>
 </div>
 )}

 {/* 文件 */}
 {fileSystem[currentPath]?.files?.length > 0 && (
 <div className="p-4 border-t">
 <h3 className="text-sm font-medium text-ink-900 mb-3 flex items-center gap-2">
 <File className="w-4 h-4" />
 {t('fileManager.files', '文件')}
 </h3>
 <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4' : 'space-y-2'}>
 {fileSystem[currentPath].files.map(file => (
 <div
 key={file.id}
 className={`group cursor-pointer ${
 viewMode === 'grid' 
 ? 'p-3 rounded-lg hover:bg-cream-100 text-center' 
 : 'flex items-center gap-3 p-3 hover:bg-cream-100 rounded-lg'
 }`}
 >
 <div className={viewMode === 'grid' ? 'mx-auto mb-2' : ''}>
 {getFileIcon(file.type)}
 </div>
 <div className={viewMode === 'grid' ? '' : 'flex-1'}>
 <div className="font-medium text-sm text-ink-900 truncate">{file.name}</div>
 <div className="text-xs text-ink-500">{file.size}</div>
 </div>
 {viewMode === 'list' && (
 <>
 <div className="text-xs text-ink-500">{file.created}</div>
 <div className="flex items-center gap-1">
 {file.type === 'audio' && (
 <button className="p-1 text-ink-400 hover:text-accent-600 transition-colors">
 <Play className="w-4 h-4" />
 </button>
 )}
 <button className="p-1 text-ink-400 hover:text-ink-600 transition-colors">
 <Download className="w-4 h-4" />
 </button>
 <button className="p-1 text-ink-400 hover:text-ink-600 transition-colors">
 <MoreVertical className="w-4 h-4" />
 </button>
 </div>
 </>
 )}
 </div>
 ))}
 </div>
 </div>
 )}

 {/* 空状态 */}
 {(!fileSystem[currentPath]?.folders?.length && !fileSystem[currentPath]?.files?.length) && (
 <div className="text-center py-12">
 <Folder className="w-16 h-16 text-ink-300 mx-auto mb-4" />
 <h3 className="text-lg font-medium text-ink-600 mb-2">
 {t('fileManager.empty.title', '文件夹为空')}
 </h3>
 <p className="text-ink-500 mb-6">
 {t('fileManager.empty.description', '开始上传文件或创建文件夹来组织你的播客素材')}
 </p>
 <div className="flex justify-center gap-3">
 <button
 onClick={() => setShowUploadModal(true)}
 className="bg-accent-600 text-white px-4 py-2 rounded-lg hover:bg-accent-700 transition-colors"
 >
 {t('fileManager.uploadFiles', '上传文件')}
 </button>
 <button
 onClick={() => setShowNewFolderModal(true)}
 className="bg-cream-50 border border-ink-300 text-ink-700 px-4 py-2 rounded-lg hover:bg-cream-100 transition-colors"
 >
 {t('fileManager.newFolder', '新建文件夹')}
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 );

 // 渲染不同视图
 const renderCurrentView = () => {
 switch (currentView) {
 case 'file-manager':
 return renderFileManager();
 case 'uploads':
 return (
 <div className="text-center py-12">
 <Upload className="w-16 h-16 text-ink-300 mx-auto mb-4" />
 <h2 className="text-xl font-semibold text-ink-600">{t('fileManager.uploads.title', '文件上传')}</h2>
 <p className="text-ink-500">{t('fileManager.uploads.description', '拖拽文件到这里上传')}</p>
 </div>
 );
 case 'audio-library':
 return (
 <div className="text-center py-12">
 <FileAudio className="w-16 h-16 text-ink-300 mx-auto mb-4" />
 <h2 className="text-xl font-semibold text-ink-600">{t('fileManager.audioLibrary.title', '音频库')}</h2>
 <p className="text-ink-500">{t('fileManager.audioLibrary.description', '管理你的音频文件')}</p>
 </div>
 );
 case 'projects':
 return (
 <div className="text-center py-12">
 <Archive className="w-16 h-16 text-ink-300 mx-auto mb-4" />
 <h2 className="text-xl font-semibold text-ink-600">{t('fileManager.projects.title', '项目文件')}</h2>
 <p className="text-ink-500">{t('fileManager.projects.description', '播客项目和工程文件')}</p>
 </div>
 );
 default:
 return renderFileManager();
 }
 };

 return (
 <div className="min-h-screen bg-cream-100">
 <div className="container mx-auto px-4 py-8">
 {renderCurrentView()}

 {/* 上传文件模态框 */}
 {showUploadModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-cream-50 rounded-2xl p-6 w-full max-w-md">
 <h3 className="text-lg font-semibold mb-4">{t('fileManager.uploadFiles', '上传文件')}</h3>
 <div className="border-2 border-dashed border-ink-300 rounded-lg p-8 text-center">
 <Upload className="w-12 h-12 text-ink-400 mx-auto mb-4" />
 <p className="text-ink-600 mb-4">{t('fileManager.dragDrop', '拖拽文件到这里或点击选择')}</p>
 <input
 type="file"
 multiple
 onChange={handleFileUpload}
 className="hidden"
 id="file-upload"
 />
 <label
 htmlFor="file-upload"
 className="bg-accent-600 text-white px-4 py-2 rounded-lg hover:bg-accent-700 transition-colors cursor-pointer"
 >
 {t('fileManager.selectFiles', '选择文件')}
 </label>
 </div>
 <div className="flex justify-end gap-3 mt-6">
 <button
 onClick={() => setShowUploadModal(false)}
 className="px-4 py-2 text-ink-600 hover:text-ink-800 transition-colors"
 >
 {t('common.cancel', '取消')}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* 新建文件夹模态框 */}
 {showNewFolderModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-cream-50 rounded-2xl p-6 w-full max-w-md">
 <h3 className="text-lg font-semibold mb-4">{t('fileManager.newFolder', '新建文件夹')}</h3>
 <input
 type="text"
 value={newFolderName}
 onChange={(e) => setNewFolderName(e.target.value)}
 placeholder={t('fileManager.folderName', '文件夹名称')}
 className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
 autoFocus
 />
 <div className="flex justify-end gap-3 mt-6">
 <button
 onClick={() => setShowNewFolderModal(false)}
 className="px-4 py-2 text-ink-600 hover:text-ink-800 transition-colors"
 >
 {t('common.cancel', '取消')}
 </button>
 <button
 onClick={handleCreateFolder}
 className="bg-accent-600 text-white px-4 py-2 rounded-lg hover:bg-accent-700 transition-colors"
 >
 {t('common.create', '创建')}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
}